import { useRouter } from "next/router";
import { useEffect } from "react";

import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { AxiosError } from "axios";

import { yupResolver } from "@hookform/resolvers/yup";
import useWebStorage, { STORAGE_KEY } from "@logics/hooks/useWebStorage";
import { SignupSchemaType } from "@domains/auth/types/authTypes";
import { signupSchema } from "@domains/auth/constants/authFormSchema";
import { useCreateRegisterMutation } from "@domains/auth/network/authMutations";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import { dataTrackEvent } from "@logics/utils/dataTrackEvent";
import { DATA_TRACKING_EVENT_NAME } from "@domains/common/constants/dataTrackEventName";
import { ToastError } from "@logics/utils/toast";
import { ERROR_CODE } from "@domains/common/constants/errors";
import { ErrorResponseType } from "@models/client";

import FormInputForPassword from "../components/FormInputForPassword/FormInputForPassword";
import SubmitButton from "../components/SubmitButton/SubmitButton";

import * as S from "./SignUpComponent.style";

const SignUpComponent = () => {
  const router = useRouter();

  const [email] = useWebStorage(STORAGE_KEY.AUTH_EMAIL, "", "sessionStorage");

  const methods = useForm<SignupSchemaType>({
    resolver: yupResolver(signupSchema, { abortEarly: false }),
    criteriaMode: "all",
    mode: "onChange",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    methods.setValue("email", email);
  }, [methods, email]);

  const signupMutation = useCreateRegisterMutation();

  const handleSubmitSignup: SubmitHandler<SignupSchemaType> = async ({
    email,
    password,
  }) => {
    try {
      await signupMutation.mutateAsync({ email, password });

      router.push(NAVIGATION_ROUTE.AUTH_COMPLETE.HREF);
    } catch (error) {
      ToastError("Failed to sign up.");

      const errorCode = (error as AxiosError<ErrorResponseType>)?.response?.data
        ?.errorCode;

      switch (errorCode) {
        case ERROR_CODE.US004:
          router.push(NAVIGATION_ROUTE.AUTH_COMPLETE.HREF);
          break;
        default:
          break;
      }

      console.error(error);
    }
  };

  useEffect(() => {
    dataTrackEvent({
      eventName: DATA_TRACKING_EVENT_NAME.AUTH.VIEW_SIGNUP_COMPLETE,
    });
  }, []);

  return (
    <S.SignUpContainer>
      {email && <S.Email name="m">{email}</S.Email>}
      <S.Title name="h3">Create a password for your new account</S.Title>
      <FormProvider {...methods}>
        <S.SignupForm onSubmit={methods.handleSubmit(handleSubmitSignup)}>
          <FormInputForPassword
            formId="password"
            label="Password"
            placeholder="Put in your password"
          />
          <SubmitButton
            type="submit"
            isLoading={methods.formState.isSubmitting}
            isReadyToSubmit={methods.formState.isValid}
          />
        </S.SignupForm>
      </FormProvider>
    </S.SignUpContainer>
  );
};

export default SignUpComponent;
